import React, { useCallback } from "react";
import { ImageComponent, ReactPortalComp } from "../../../../../components";

import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import CommonModalDweb from "../../../../../components/CommonModalDweb/CommonModalDweb";
import FormattedText, {
  getPlainText,
} from "../../../../../components/FormattedText/FormattedText";
import { ChevronUpIcon } from "../../../../../icons";
import cancelGreyBg from "../../../../../icons/cancelGreyBg.svg";
import type { ClaimReasonData, ClaimReasonNode } from "../../../types";
import {
  Backdrop,
  ChevronWrapper,
  CloseButton,
  ModalBody,
  ReasonContent,
  ReasonRow,
  ReasonSubtitle,
  ReasonTitle,
  SheetHeader,
  SheetPanel,
  SheetTitle,
} from "./ReasonPickerModal.style";

interface ReasonPickerModalProps {
  reasonData: ClaimReasonData;
  onSelectNode: (node: ClaimReasonNode) => void;
  onClose: () => void;
}

export function ReasonPickerModal({
  reasonData,
  onSelectNode,
  onClose,
}: ReasonPickerModalProps) {
  const device = useBreakpointV2();
  const nodes = reasonData?.nodes ?? [];
  const title = getPlainText(reasonData?.title).trim();

  const handleNodeClick = useCallback(
    (node: ClaimReasonNode) => {
      if (node.isDisabled) return;
      onSelectNode(node);
      onClose();
    },
    [onSelectNode, onClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (nodes.length === 0) return null;

  const reasonList = (
    <ModalBody>
      {nodes.map((node) => {
        const nodeTitle = getPlainText(node?.title).trim();
        if (!nodeTitle) return null;
        const disabled = Boolean(node.isDisabled);

        return (
          <ReasonRow
            key={node.id ?? nodeTitle}
            onClick={() => handleNodeClick(node)}
            type="button"
            disabled={disabled}
            aria-disabled={disabled}
            $disabled={disabled}
          >
            <ReasonContent>
              <ReasonTitle $disabled={disabled}>
                <FormattedText value={node.title} />
              </ReasonTitle>
              {getPlainText(node.subtitle).trim() ? (
                <ReasonSubtitle>
                  <FormattedText value={node.subtitle} />
                </ReasonSubtitle>
              ) : null}
            </ReasonContent>
            <ChevronWrapper aria-hidden="true">
              <ChevronUpIcon
                color={disabled ? theme.greyText4 : theme.blueText}
                cursor="default"
              />
            </ChevronWrapper>
          </ReasonRow>
        );
      })}
    </ModalBody>
  );

  if (device === "sm") {
    return (
      <ReactPortalComp>
        <Backdrop onClick={handleBackdropClick}>
          <SheetPanel
            $maxHeight="70vh"
            $padding="20px 0 0"
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <SheetHeader>
              <SheetTitle>
                <FormattedText value={reasonData?.title} />
              </SheetTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <ImageComponent
                  src={cancelGreyBg.src}
                  width="24px"
                  height="24px"
                  alt="Close"
                />
              </CloseButton>
            </SheetHeader>
            {reasonList}
          </SheetPanel>
        </Backdrop>
      </ReactPortalComp>
    );
  }

  return (
    <CommonModalDweb
      customTitle={title}
      handleClick={onClose}
      customWidth="460px"
      customHeight="auto"
    >
      {reasonList}
    </CommonModalDweb>
  );
}
